import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { List, fromJS } from 'immutable';
import axios from 'axios';

import {
    resultsConfigSelector,
    sortedAndFilteredProductsSelector,
    filteredOutProductsSelector,
    describedProductsSelector,
    getAvailableReturnedBrands,
    omittedBrandsSelector,
    locationQuerySelector,
    quoteFormSelector
} from 'ctm-selectors';
import { get, pageContext } from 'ctm-util-functions';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';
import { getHasSavedResults } from 'ctm-selectors/selectors/results';
import { ProductCompareContainer } from '../../product_compare';
import { QuickPrice } from './quick_price/quick_price';
import { Features } from './features/features';
import { ResultsControls } from '../results_controls/results_controls';

/**
 * A generic modal container that formats, prepares and passes props to our visual modal component
 * @param {Object} props The properties
 * @returns {_ComparableResults} A results component
 */
const _ComparableResults = (props) => {
    const { experimentId, journeyId, campaignId, displayMode } = props;
    const brandContentFactory = useBrandContentFactory();
    const showNoResultsOverlayGenericMessage = brandContentFactory.showNoResultsOverlayGenericMessage();

    /**
     * Before rendering set to store minProducts and isAvailableResults
     * @todo: should this be in here?? can it be defined in schema or vertical reducer?
     */
    useEffect(() => {
        const minProducts = get(props, 'visualProps.productCompare.minProducts', false);
        if (minProducts) {
            setResultsConfigValue('minProducts', minProducts);
        }

        if (experimentId === 'omitProviders') {
            postMartechData({ journeyId, experimentId });
        }

        if (campaignId) {
            postMartechData({ journeyId, campaignId });
        }

        setResultsConfigValue('isAvailableResults', !(describedResults.isEmpty() || describedResults.size === unavailableBrands.size));
    }, [experimentId, journeyId, campaignId]);

    /**
     * Renders the appropriate results container depending on the display mode stored in state
     * @returns {Node} a results container
     */
    const renderResultsMode = () => {
        const breakpoint = pageContext.getProps().pageState.breakpoint;
        const {
            quotes,
            filteredQuotes,
            unavailableBrands,
            visualProps,
            contentProps,
            hideHighExcess,
            excessFilterValue
        } = props;

        const { rowLayout } = contentProps;
        const newQuotes = getDisplayableQuotes(quotes, hideHighExcess, excessFilterValue);
        const newFilteredQuotes = getDisplayableQuotes(filteredQuotes, hideHighExcess, excessFilterValue, 'newFilteredQuotes');
        const resultProps = { quotes: newQuotes, unavailableBrands, breakpoint, filteredQuotes: newFilteredQuotes };

        if (displayMode === null) {
            const defaultDisplayMode = brandContentFactory.resultsScreenDefaultDisplayMode(getHasSavedResults(), experimentId);
            setResultsConfigValue('displayMode', defaultDisplayMode);
            setResultsConfigValue('previousDisplayMode', defaultDisplayMode);
        }

        switch (displayMode) {
            case 'row':
                return <QuickPrice layout={rowLayout} {...resultProps} />;
            case 'column':
                return <Features {...visualProps} {...resultProps} />;
            case null:
                return null;
            default:
                // eslint-disable-next-line no-console
                console.error(`No results mode defined for: ${displayMode}`);
                return null;
        }
    };

    /**
     * Renders an no results overlay when there is no results to show
     * @param {String} layout The overlay layout.
     * @returns {Node} a no results overlay
     */
    const renderNoResults = (layout) => {
        // It is okay to hard code the component/layout key here as the key should generic and each vertical should use
        // the same key as much as possible
        const { director,  models } = pageContext.getProps();
        // some verticals like Energy doesn't render this, so check if the schema exists
        const isRenderingControl = models.componentSchemaModel.componentSchemaExists('layoutResultsControlNoResults');
        return layout ? (
            <>
                {isRenderingControl ? <ResultsControls shouldAffix={false} layout="component_layoutResultsControlNoResults" /> : null}
                {director.constructComponents([layout])}
            </>
        ) : null;
    };

    const renderCompareBasket = () => {
        const { visualProps } = props;
        const compareEnabled = get(props, 'visualProps.productCompare', false);
        if (compareEnabled === false) {
            return null;
        }
        const { minProducts, maxProducts } = visualProps.productCompare;
        return (<ProductCompareContainer contentProps={{ minProducts, maxProducts }} />);
    };

    /**
     * Post journey id to martech service to keep track of experiment participants, this
     * is used for email marketing.
     * @param {Object} martechData martechData to post to the martech tracking url
     */
    const postMartechData = (martechData) => {
        const { martechTrackingURL } = props;
        axios.post(martechTrackingURL, martechData)
            .then((response) => {
                let log = 'Posted ';
                Object.keys(martechData).forEach((key) => {
                    log += `${key}: ${martechData[key]}, `;
                });
                log += 'to experiment endpoint';

                // eslint-disable-next-line no-console
                console.log(log);
                // eslint-disable-next-line no-console
                console.log(response);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(`Error posting to experiment endpoint: ${error}`);
            });
    };

    /**
     * Dispatch a SET_RESULTS_CONFIG_VALUE action to update the store
     * @param {Object} key key value to be set within the store
     * @param {Object} value value to be set to the key within the store
     */
    const setResultsConfigValue = (key, value) => {
        const { dispatch } = props;
        dispatch({ type: 'SET_RESULTS_CONFIG_VALUE', key, value });
    };

    /**
     * filter quotes by Excess Filter if required
     * @param {Array} quotes - The array of quotes to render within the affixed header
     * @param {Boolean} hideHighExcess - should we filter the excess
     * @param {Integer} excessFilterValue - current excess filter value
     * @param {string} invoker - tells us where it got invoked from.
     * @returns {Array} of quotes
     */
    const getDisplayableQuotes = (quotes, hideHighExcess, excessFilterValue, invoker) => {
        if (hideHighExcess) {
            if (invoker === 'newFilteredQuotes' && pageContext.getVertical() === 'car-insurance') { return List([]); }
            return List(quotes.filter((quote) => { return quote.get('productMeta').get('excess') <= excessFilterValue; }));
        }
        return quotes;
    };

    /**
     * Render the correct results container including the product compare container for comparing results
     * @returns {Node} the results and product compare component
     */

    const {
        quotes,
        unavailableBrands,
        describedResults,
        isDisplayingResults,
        hasAvailableBrands,
        hideHighExcess,
        excessFilterValue
    } = props;
    const hasQuotes = getDisplayableQuotes(quotes, hideHighExcess, excessFilterValue).size > 0;
    const hasUnavailableBrands = !unavailableBrands.isEmpty();

    // If no quotes at all, no unavailable brands and is not displaying results
    // don't render the results layout as it hasn't come back from the server yet
    if (!hasQuotes && !hasUnavailableBrands && !isDisplayingResults) {
        return null;
    }

    // Define Min and Max products within ProductCompareContainer
    let resultsLayout;
    if ((!hasQuotes && hasUnavailableBrands) || describedResults.isEmpty()) {
        if (showNoResultsOverlayGenericMessage) {
            resultsLayout = renderNoResults('component_noResultsOverlayGeneric');
        } else {
            resultsLayout = renderNoResults('component_noResultsOverlay');
        }
    } else {
        resultsLayout = renderResultsMode();
    }

    // Check if user has filtered out all results.
    if (!describedResults.isEmpty() && hasAvailableBrands && !hasQuotes) {
        resultsLayout = renderNoResults('component_noFilteredResultsOverlay');
    }

    return (
        <>
            {resultsLayout}
            {renderCompareBasket()}
        </>
    );
};

_ComparableResults.propTypes = {
    /* these props come from schema config */
    contentProps: PropTypes.shape({
        rowLayout: PropTypes.array
    }),
    visualProps: PropTypes.shape({
        productCompare: PropTypes.shape({
            minProducts: PropTypes.number,
            maxProducts: PropTypes.number
        }),
        columnsPerPage: PropTypes.shape({
            xs: PropTypes.number,
            sm: PropTypes.number
        }),
        gridProps: PropTypes.shape({
            xs: PropTypes.number,
            sm: PropTypes.number,
            md: PropTypes.number,
            lg: PropTypes.number,
            xl: PropTypes.number
        })
    }),
    /* these props come from state */
    sorting: PropTypes.object,
    displayMode: PropTypes.string,
    quotes: PropTypes.instanceOf(List).isRequired,
    filteredQuotes: PropTypes.instanceOf(List).isRequired,
    isDisplayingResults: PropTypes.bool,
    describedResults: PropTypes.object,
    unavailableBrands: PropTypes.object,
    hasAvailableBrands: PropTypes.bool,
    dispatch: PropTypes.func,
    martechTrackingURL: PropTypes.string,
    journeyId: PropTypes.number,
    experimentId: PropTypes.string,
    campaignId: PropTypes.string,
    hideHighExcess: PropTypes.bool,
    excessFilterValue: PropTypes.number
};

/**
 * Map rules to our local component props
 * @param {Object} state - the application state
 * @param {Object} props - the component props
 * @returns {Object} - the set of mapped props for the container
 */
const mapStateToProps = (state, props) => {
    const resultsConfig = resultsConfigSelector(state);
    const omittedBrands = omittedBrandsSelector(state) || [];
    const queries = locationQuerySelector(state).toJS();
    const form = quoteFormSelector(state);

    // Get the sorted quotes based on store config
    const quotes = sortedAndFilteredProductsSelector(state).filter((quote) => {
        const providerCode = quote.getIn(['providerMeta', 'code']);
        return !omittedBrands.some((omittedProviderMeta) =>  omittedProviderMeta.get('code') === providerCode);
    });
    let filteredQuotes = List();
    if (pageContext.getVertical() === 'home-contents-insurance' || pageContext.getVertical() === 'car-insurance') {
        filteredQuotes = filteredOutProductsSelector(state);
    }

    const availableBrandCodes = getAvailableReturnedBrands(state, true)
        .reduce((acc, quote) => {
            const providerCode = quote.getIn(['providerMeta', 'code']);
            if (!acc.includes(providerCode)) {
                acc.push(providerCode);
            }
            return acc;
        }, []);

    const unavailableBrands = getAvailableReturnedBrands(state, false)
    // unavailableBrands contains any products that aren't available, we want to check if we have
    // other available products for the same brand, and if so, filter that brand out of this list
        .filter((quote) => {
            const providerCode = quote.getIn(['providerMeta', 'code']);
            return !availableBrandCodes.includes(providerCode);
        })
        .concat(omittedBrands.map((providerMeta) => fromJS({ providerMeta })))
        .sort(((a, b) => {
            const aCode = a.getIn(['providerMeta', 'code']);
            const bCode = b.getIn(['providerMeta', 'code']);
            if (aCode < bCode) {
                return -1;
            }
            if (aCode > bCode) {
                return 1;
            }
            return 0;
        }));

    const hasAvailableBrands = !getAvailableReturnedBrands(state, true).isEmpty();
    const describedResults = describedProductsSelector(state);
    const isDisplayingResults = resultsConfig.get('isDisplayingResults');
    const displayMode = resultsConfig.get('displayMode');
    const journeyId = get(state, 'user.journey.transaction.journeyId');
    const experimentId = get(state, 'page.experimentId');
    const isCar = get(state, 'page.vertical') === 'car-insurance';
    const campaignId = queries.campaign_id;
    const hideHighExcess = isCar && get(state, 'page.isHideHighExcess');
    const excessFilterValue = parseInt(form.getIn(['coverDetail', 'requestedExcess']), 10);

    // required for energy!
    const sortingConfig = resultsConfig.get('sorting');

    return {
        displayMode,
        quotes,
        filteredQuotes,
        unavailableBrands,
        hasAvailableBrands,
        describedResults,
        isDisplayingResults,
        journeyId,
        experimentId,
        campaignId,
        sorting: {
            sortBy: sortingConfig.get('sortBy'),
            sortDir: sortingConfig.get('sortDir'),
            sortType: sortingConfig.get('sortType')
        },
        hideHighExcess,
        excessFilterValue
    };
};

const ComparableResults = connect(mapStateToProps)(_ComparableResults);
export { _ComparableResults, ComparableResults };
